import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Landmark, Building2, Users, AlertTriangle, RefreshCw } from 'lucide-react';
import { shortDate } from '@/lib/utils';
import {
  getWhopSubscriptionPayments,
  getWhopSubscriptionStats,
  reconcileWhopSubscriptionPayments,
  type WhopSubscriptionPaymentRow,
} from '@/lib/workerApi';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { DataPagination } from '@/components/DataPagination';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

// Whop payment lifecycle statuses. 'paid' is the only one that collected money;
// the rest are shown so a missing charge can be traced rather than guessed at.
type StatusTab = 'all' | 'paid' | 'open' | 'pending' | 'void' | 'uncollectible';
const STATUS_TABS: { key: StatusTab; label: string }[] = [
  { key: 'all',           label: 'All' },
  { key: 'paid',          label: 'Paid' },
  { key: 'open',          label: 'Open' },
  { key: 'pending',       label: 'Pending' },
  { key: 'uncollectible', label: 'Uncollectible' },
  { key: 'void',          label: 'Void' },
];

const TIER_LABEL: Record<string, string> = {
  free: 'Free',
  starter: 'Starter',
  pro: 'Pro',
  pro_annual: 'Pro (Annual)',
  agency: 'Agency',
};

function usd(cents: number | null | undefined) {
  if (cents === null || cents === undefined) return null;
  return `$${(cents / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function StatusBadge({ row }: { row: WhopSubscriptionPaymentRow }) {
  // Prefer Whop's friendly substatus ('succeeded'/'failed'/...) and fall back to
  // the lifecycle status when it's absent.
  const label = row.substatus || row.status || 'unknown';
  const cls =
    label === 'succeeded' || label === 'paid'
      ? 'bg-green-100 text-green-800 border-green-200'
      : label === 'failed' || label === 'uncollectible'
        ? 'bg-red-100 text-red-800 border-red-200'
        : label === 'pending' || label === 'open'
          ? 'bg-amber-100 text-amber-800 border-amber-200'
          : label === 'void' || label === 'canceled'
            ? 'bg-slate-100 text-slate-700 border-slate-200'
            : 'bg-gray-100 text-gray-700 border-gray-200';
  return <Badge className={`border ${cls} capitalize`}>{label}</Badge>;
}

/**
 * Whop's `billing_reason` is the renewal signal. Full documented enum:
 * 'subscription_create' (first charge — signup or upgrade) |
 * 'subscription_cycle' (auto-renewal on an existing subscription) |
 * 'subscription_update' (mid-cycle plan change) | 'one_time' | 'manual' |
 * 'subscription'. Only the first three appear on subscription rows in
 * practice; the rest render their raw value rather than guessing a label.
 */
function BillingReasonBadge({ reason }: { reason: string | null }) {
  if (!reason) return <span className="text-xs text-muted-foreground">—</span>;
  switch (reason) {
    case 'subscription_cycle':
      return <Badge className="bg-emerald-100 text-emerald-800 border border-emerald-200 text-[10px] px-1.5 py-0">Renewal</Badge>;
    case 'subscription_create':
      return <Badge className="bg-blue-100 text-blue-800 border border-blue-200 text-[10px] px-1.5 py-0">New</Badge>;
    case 'subscription_update':
      return <Badge className="bg-purple-100 text-purple-800 border border-purple-200 text-[10px] px-1.5 py-0">Plan Change</Badge>;
    default:
      return <Badge variant="outline" className="text-[10px] px-1.5 py-0 capitalize">{reason.replace(/_/g, ' ')}</Badge>;
  }
}

/** Card brand + last 4 when a card was used; otherwise the raw payment method type. */
function MethodCell({ row }: { row: WhopSubscriptionPaymentRow }) {
  if (row.card_brand) {
    return (
      <span className="text-xs whitespace-nowrap">
        <span className="capitalize">{row.card_brand}</span>
        {row.card_last4 && <span className="text-muted-foreground"> •••• {row.card_last4}</span>}
      </span>
    );
  }
  if (row.payment_method_type) {
    return <span className="text-xs text-muted-foreground capitalize">{row.payment_method_type}</span>;
  }
  return <span className="text-xs text-muted-foreground">—</span>;
}

function TierBadge({ tier }: { tier: WhopSubscriptionPaymentRow['tier'] }) {
  if (!tier) return <span className="text-xs text-muted-foreground">—</span>;
  const cls =
    tier === 'agency'
      ? 'bg-purple-100 text-purple-800 border-purple-200'
      : tier === 'pro' || tier === 'pro_annual'
        ? 'bg-blue-100 text-blue-800 border-blue-200'
        : 'bg-slate-100 text-slate-700 border-slate-200';
  return <Badge className={`border ${cls}`}>{TIER_LABEL[tier] ?? tier}</Badge>;
}

/**
 * Who the charge belongs to. Agency ($997) payments are attributed to the
 * account, brand subscriptions to the brand. Renewals carry no metadata at all,
 * so those fall back to the Whop user — shown as such rather than left blank.
 */
function PayerCell({ row }: { row: WhopSubscriptionPaymentRow }) {
  const name = row.account_name ?? row.brand_name;
  if (name) {
    return (
      <div>
        <div className="font-medium truncate">{name}</div>
        {row.account_name && <div className="text-[11px] text-muted-foreground">Agency account</div>}
        {row.user_email && !row.account_name && (
          <div className="text-[11px] text-muted-foreground truncate">{row.user_email}</div>
        )}
      </div>
    );
  }
  return (
    <div>
      <div className="font-medium truncate">{row.user_name ?? row.user_email ?? '—'}</div>
      <div className="text-[11px] text-muted-foreground">
        {row.brand_id || row.account_id ? 'name unresolved' : 'via Whop user (renewal)'}
      </div>
    </div>
  );
}

type StatAccent = 'emerald' | 'sky' | 'purple';

// Same shape as HybridLedger's LedgerStatCard accents (tile/value/glow), plus
// a `pill` tone for the paid-count badge — kept local rather than shared
// since this file is the only place that needs the sky/purple pair (brand vs
// agency), and HybridLedger's set doesn't have them.
const STAT_ACCENTS: Record<StatAccent, { tile: string; value: string; glow: string; pill: string; bar: string }> = {
  emerald: {
    tile: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400',
    value: 'text-emerald-600 dark:text-emerald-400',
    glow: 'from-emerald-500/[0.12]',
    pill: 'bg-emerald-100 text-emerald-700 border border-emerald-200',
    bar: 'bg-emerald-500',
  },
  sky: {
    tile: 'bg-sky-500/10 text-sky-600 dark:text-sky-400',
    value: 'text-foreground',
    glow: 'from-sky-500/[0.12]',
    pill: 'bg-sky-100 text-sky-700 border border-sky-200',
    bar: 'bg-sky-500',
  },
  purple: {
    tile: 'bg-purple-500/10 text-purple-600 dark:text-purple-400',
    value: 'text-foreground',
    glow: 'from-purple-500/[0.12]',
    pill: 'bg-purple-100 text-purple-700 border border-purple-200',
    bar: 'bg-purple-500',
  },
};

/**
 * Premium tile for the three revenue totals — a rounded-xl card with a
 * blurred corner glow (matches HybridLedger's LedgerStatCard, the most
 * "finished" stat treatment already in the app) instead of the flat
 * bordered box the finance panels default to. The paid-charge count is a
 * colored pill, not plain caption text, so it reads as a real number rather
 * than fine print; an optional segmented bar shows this figure's share of
 * the total.
 */
function StatCard({ icon: Icon, label, value, count, countLabel = 'paid', truncated, sub, accent, segments, loading }: {
  icon: React.ElementType;
  label: string;
  value: React.ReactNode;
  /** Paid-charge count for the pill. Omitted while loading. */
  count?: number;
  countLabel?: string;
  /** Whop scan hit its cap — the count and the totals it's built from are a
   *  floor, not the true number. */
  truncated?: boolean;
  sub?: string;
  accent: StatAccent;
  /** This card's share of the total, as filled segments (0-100 each) under
   *  the pill row — e.g. brand's cut in blue, agency's in purple. */
  segments?: { pct: number; className: string }[];
  loading?: boolean;
}) {
  const a = STAT_ACCENTS[accent];
  return (
    <div className="group relative overflow-hidden rounded-xl border bg-card p-5 shadow-sm transition-shadow duration-200 hover:shadow-elevate-lg">
      <div className={`pointer-events-none absolute -right-10 -top-10 h-32 w-32 rounded-full bg-gradient-to-br ${a.glow} to-transparent blur-2xl transition-opacity duration-200 group-hover:opacity-125`} />
      <div className="relative space-y-3">
        <div className="flex items-center gap-2">
          <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${a.tile}`}>
            <Icon className="h-4 w-4" />
          </span>
          <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</p>
        </div>

        {loading ? (
          <Skeleton className="h-9 w-32" />
        ) : (
          <p className={`text-3xl font-bold leading-none tracking-tight tabular-nums ${a.value}`}>{value}</p>
        )}

        <div className="flex min-h-[22px] flex-wrap items-center gap-2">
          {loading ? (
            <Skeleton className="h-[22px] w-24 rounded-full" />
          ) : (
            <>
              {count !== undefined && (
                <TooltipProvider>
                  <Tooltip delayDuration={200}>
                    <TooltipTrigger asChild>
                      <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-bold tabular-nums ${a.pill} ${truncated ? 'cursor-help' : ''}`}>
                        {count.toLocaleString()}{truncated ? '+' : ''} {countLabel}
                      </span>
                    </TooltipTrigger>
                    {truncated && (
                      <TooltipContent side="top" className="max-w-xs">
                        More subscription payments exist on Whop than were scanned — this count (and the
                        amount above) is a floor, not the true total.
                      </TooltipContent>
                    )}
                  </Tooltip>
                </TooltipProvider>
              )}
              {sub && <span className="text-xs text-muted-foreground">{sub}</span>}
            </>
          )}
        </div>

        {!loading && segments && segments.length > 0 && (
          <div className="flex h-1 w-full overflow-hidden rounded-full bg-muted">
            {segments.map((s, i) => (
              <div
                key={i}
                className={`h-full transition-all duration-500 ${s.className}`}
                style={{ width: `${Math.max(0, Math.min(s.pct, 100))}%` }}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Subscription charges read LIVE from Whop, scoped to our subscription
 * plans/products. See SystemSubscriptionsPanel for the same charges as our own
 * DB recorded them (faster, real revenue totals, but only as complete as the
 * webhook's coverage).
 */
export default function WhopSubscriptionsPanel() {
  const [statusTab, setStatusTab]     = useState<StatusTab>('all');
  const [page, setPage]               = useState(1);
  const [pageSize, setPageSize]       = useState(25);
  // On-demand only: this runs a full scan plus one Whop fetch per withheld
  // payment, so it must never fire as part of the normal page load.
  const [reconcile, setReconcile]     = useState<{ loading: boolean; result: unknown; error: string | null }>(
    { loading: false, result: null, error: null }
  );

  async function runReconcile() {
    setReconcile({ loading: true, result: null, error: null });
    try {
      const result = await reconcileWhopSubscriptionPayments({ max_scan: 1000, inspect: 10 });
      setReconcile({ loading: false, result, error: null });
    } catch (e) {
      setReconcile({ loading: false, result: null, error: (e as Error).message });
    }
  }

  // Listing: paginated rows for the table. `statuses` is the only filter —
  // it's the one Whop applies server-side. Search and date-range filters were
  // tried and removed: Whop's v2 API has no server-side param for either, so
  // supporting them meant fetching the entire unfiltered scan and discarding
  // rows in code, which is exactly the fetch-then-filter shape this page
  // otherwise doesn't accept. Excludes statusTab/page/pageSize from the stats
  // query below on purpose — see that query's comment.
  const { data, isLoading, isFetching, error, refetch } = useQuery({
    queryKey: ['whop-subscription-payments', statusTab, page, pageSize],
    queryFn: () => getWhopSubscriptionPayments({
      limit: pageSize,
      offset: (page - 1) * pageSize,
      statuses: statusTab === 'all' ? undefined : [statusTab],
    }),
    // Live Whop calls — don't refetch on every window focus.
    staleTime: 60_000,
    refetchOnWindowFocus: false,
    retry: 1,
  });

  // Stats: revenue totals + every status's count at once, for the stat cards
  // and the tab badges. A separate, parallel call from the listing query
  // above — its own full Whop scan, so a slow stats computation never blocks
  // the table from rendering (or vice versa). Deliberately excludes
  // statusTab/page/pageSize: it always covers every status (Whop's v2 status
  // filter applies server-side, so passing the active tab's status here
  // would zero out every other tab's badge count), and it's the same
  // regardless of which page is showing.
  const { data: statsData, isLoading: statsLoading } = useQuery({
    queryKey: ['whop-subscription-stats'],
    queryFn: () => getWhopSubscriptionStats(),
    staleTime: 60_000,
    refetchOnWindowFocus: false,
    retry: 1,
  });

  const rows       = data?.rows ?? [];
  const totalItems = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const statusCounts = statsData?.status_counts ?? {};

  // Brand vs agency's share of the total paid amount, for the stat cards'
  // proportion bars. Zero (not NaN) while stats haven't loaded or nothing's
  // been collected yet.
  const totalCents  = statsData?.stats.total_amount_cents ?? 0;
  const brandCents  = statsData?.stats.brand_amount_cents ?? 0;
  const agencyCents = statsData?.stats.agency_amount_cents ?? 0;
  const brandShare  = totalCents > 0 ? (brandCents / totalCents) * 100 : 0;
  const agencyShare = totalCents > 0 ? (agencyCents / totalCents) * 100 : 0;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <p className="text-sm text-muted-foreground">
          Read live from Whop, scoped to our subscription plans only — the launch product and
          creator-hire charges are excluded.
        </p>
        <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isFetching}>
          <RefreshCw className={`mr-2 h-3.5 w-3.5 ${isFetching ? 'animate-spin' : ''}`} />
          {isFetching ? 'Refreshing' : 'Refresh'}
        </Button>
      </div>

      {/* Every total below is 'paid' charges only — the rest (open/pending/void/
          uncollectible) never collected anything, so they're excluded from the
          sum, not just uncounted. Needs no per-payment enrichment: v2's
          final_amount was verified against v1's settlement_amount (the only
          discount-and-tax-aware figure) across every sampled payment,
          including every refund found — so the same full scan the listing
          query already pays for is the only cost here. The pill under each
          amount is the same 'paid'-only population — how many charges the
          dollar figure is made of, not the row count in the listing below
          (which still shows every status) — and the bar under Brand/Agency
          (plus the split under Total) is that category's share of the total
          paid amount. */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
        <StatCard
          icon={Landmark}
          label="Total Amount"
          value={statsLoading ? '—' : usd(totalCents)}
          count={statsData?.stats.total_paid_count}
          truncated={statsData?.truncated}
          sub={statsLoading || totalCents === 0 ? undefined : `${brandShare.toFixed(0)}% brand · ${agencyShare.toFixed(0)}% agency`}
          accent="emerald"
          segments={[
            { pct: brandShare, className: 'bg-sky-500' },
            { pct: agencyShare, className: 'bg-purple-500' },
          ]}
          loading={statsLoading}
        />
        <StatCard
          icon={Building2}
          label="Brand Subscriptions"
          value={statsLoading ? '—' : usd(brandCents)}
          count={statsData?.stats.brand_paid_count}
          truncated={statsData?.truncated}
          sub="starter, pro, pro annual"
          accent="sky"
          segments={[{ pct: brandShare, className: 'bg-sky-500' }]}
          loading={statsLoading}
        />
        <StatCard
          icon={Users}
          label="Agency Subscriptions"
          value={statsLoading ? '—' : usd(agencyCents)}
          count={statsData?.stats.agency_paid_count}
          truncated={statsData?.truncated}
          sub="agency plan"
          accent="purple"
          segments={[{ pct: agencyShare, className: 'bg-purple-500' }]}
          loading={statsLoading}
        />
      </div>

      {/* A payment on a product or plan id that isn't in the worker config would
          otherwise just be absent — which reads as "no revenue" rather than
          "misconfigured". Surface it instead. */}
      {/* {!!data?.diagnostics.excluded_unrecognised && (
        <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm">
          <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
          <div className="text-amber-800 space-y-1">
            <p>
              <strong>{data.diagnostics.excluded_unrecognised}</strong> payment(s) were skipped
              because their Whop product/plan id isn&apos;t in the worker config, so a tier may be
              missing below. Compare the ids Whop returned against wrangler.toml.
            </p>
            <p className="text-xs font-mono break-all">
              products seen: {data.diagnostics.seen_product_ids.join(', ') || '—'}
            </p>
            <p className="text-xs font-mono break-all">
              plans seen: {data.diagnostics.seen_plan_ids.join(', ') || '—'}
            </p>
          </div>
        </div>
      )} */}

      {(data?.truncated || statsData?.truncated) && (
        <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm">
          <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
          <p className="text-amber-800">
            More subscription payments exist on Whop than were scanned, so the count{data?.truncated && statsData?.truncated ? 's' : ''} above{' '}
            {data?.truncated && statsData?.truncated ? 'are floors' : 'is a floor'} rather than the true total.
          </p>
        </div>
      )}

      {/* Reconciliation aid: when this page's count disagrees with the Whop
          dashboard, these numbers say whether Whop withheld the rows or we
          filtered them. raw_* is pre-filter, kept_* is post-filter, and the
          excluded_* counters account for every difference between them. */}
      {/* {data && (
        <details className="rounded-lg border bg-muted/30 px-4 py-3 text-sm">
          <summary className="cursor-pointer font-medium text-muted-foreground">
            Scan reconciliation — {data.diagnostics.distinct_returned} distinct payments returned by
            Whop, {totalItems} kept
          </summary>
          <div className="mt-3 grid gap-4 sm:grid-cols-2">
            <div className="space-y-1">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Accounting</p>
              <ul className="text-xs space-y-0.5 tabular-nums">
                <li>
                  <strong>{data.diagnostics.distinct_returned}</strong> distinct payments returned by Whop
                  <span className="text-muted-foreground"> — compare this to a Whop dashboard count</span>
                </li>
                <li>Kept — matched by product: <strong>{data.diagnostics.matched_by_product}</strong></li>
                <li>Kept — matched by plan: <strong>{data.diagnostics.matched_by_plan}</strong></li>
                <li>Excluded — hire/launch product: <strong>{data.diagnostics.excluded_non_subscription_product}</strong></li>
                <li>Excluded — unrecognised id: <strong>{data.diagnostics.excluded_unrecognised}</strong></li>
                <li>Excluded — nothing ever owed (trial placeholders): <strong>{data.diagnostics.excluded_zero_value}</strong></li>
                <li>Dropped — no payment id: <strong>{data.diagnostics.dropped_missing_id}</strong></li>
                <li className="text-muted-foreground pt-1">
                  {data.diagnostics.raw_returned} rows across {data.diagnostics.passes.length} passes
                  {' '}({data.diagnostics.raw_returned - data.diagnostics.distinct_returned} were the
                  same payment seen by more than one pass — expected, not a loss)
                </li>
              </ul>
              <div className="pt-2">
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Per pass (one filter id each)</p>
                <ul className="text-xs font-mono space-y-0.5 break-all">
                  {data.diagnostics.passes.map((p, i) => (
                    <li key={i}>{p.filter}: <strong>{p.returned}</strong> returned, {p.new_unique} new</li>
                  ))}
                </ul>
              </div>
            </div>
            <div className="space-y-2">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Returned per product (pre-filter)</p>
                <ul className="text-xs font-mono space-y-0.5 break-all">
                  {Object.entries(data.diagnostics.raw_by_product).map(([k, v]) => (
                    <li key={k}>{k}: <strong>{v}</strong>{data.diagnostics.kept_by_product[k] !== undefined && ` → kept ${data.diagnostics.kept_by_product[k]}`}</li>
                  ))}
                </ul>
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Kept per plan</p>
                <ul className="text-xs font-mono space-y-0.5 break-all">
                  {Object.entries(data.diagnostics.kept_by_plan).map(([k, v]) => (
                    <li key={k}>{k}: <strong>{v}</strong></li>
                  ))}
                </ul>
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Kept per Whop status</p>
                <ul className="text-xs font-mono space-y-0.5">
                  {Object.entries(data.diagnostics.kept_by_status).map(([k, v]) => (
                    <li key={k}>{k}: <strong>{v}</strong></li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

       
          <div className="mt-4 border-t pt-3">
            <div className="flex items-center gap-3 flex-wrap">
              <Button variant="outline" size="sm" onClick={runReconcile} disabled={reconcile.loading}>
                {reconcile.loading ? 'Reconciling…' : 'Reconcile against database'}
              </Button>
              <span className="text-xs text-muted-foreground">
                Diffs this scan against the webhook-recorded ledger, then fetches withheld payments
                by id. Slow — runs on demand only.
              </span>
            </div>
            {reconcile.error && (
              <p className="mt-2 text-xs text-red-700">{reconcile.error}</p>
            )}
            {!!reconcile.result && (
              <pre className="mt-2 max-h-96 overflow-auto rounded bg-background border p-3 text-[11px] leading-relaxed">
                {JSON.stringify(reconcile.result, null, 2)}
              </pre>
            )}
          </div>
        </details>
      )} */}

      <div className="rounded-lg border bg-card">
        <div className="flex flex-col gap-2 border-b px-4 pt-1 pb-3">
          <div className="flex items-center gap-1 flex-wrap">
            {STATUS_TABS.map(({ key, label }) => {
              // "All" isn't itself a Whop status, so it's not a key in
              // status_counts — sum every real status instead.
              const count = key === 'all'
                ? statsData?.total_count ?? Object.values(statusCounts).reduce((s, n) => s + Number(n), 0)
                : Number(statusCounts[key] ?? 0);
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => { setStatusTab(key); setPage(1); }}
                  className={`px-3 py-2.5 text-sm font-medium transition-colors flex items-center gap-1.5 ${
                    statusTab === key
                      ? 'border-b-2 border-foreground text-foreground'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  {label}
                  <span className={`text-xs rounded-full px-1.5 py-0.5 tabular-nums ${statusTab === key ? 'bg-foreground/10 text-foreground' : 'bg-muted text-muted-foreground'}`}>
                    {statsLoading ? '…' : count}
                  </span>
                </button>
              );
            })}
          </div>
          {/* No search box, no date range: Whop's v2 API supports neither
              server-side, and faking either meant fetching the entire
              unfiltered scan and discarding rows in code — the status tabs
              above are the only filter Whop itself actually applies. */}
        </div>

        {error ? (
          <div className="flex flex-col items-center justify-center py-16 text-sm gap-3 px-6 text-center">
            <AlertTriangle className="h-10 w-10 text-red-400" />
            <p className="font-medium text-red-700">Could not load subscription payments from Whop.</p>
            <p className="text-muted-foreground max-w-md">{(error as Error).message}</p>
            <Button variant="outline" size="sm" onClick={() => refetch()}>Try again</Button>
          </div>
        ) : isLoading ? (
          <div className="p-6 space-y-3">
            {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}
          </div>
        ) : totalItems === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-muted-foreground text-sm gap-2">
            <Landmark className="h-10 w-10 opacity-25 mb-2" />
            <p className="font-medium">No subscription payments found.</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="whitespace-nowrap">Brand / Account</TableHead>
                    <TableHead className="whitespace-nowrap">Plan</TableHead>
                    <TableHead className="whitespace-nowrap">Type</TableHead>
                    <TableHead className="whitespace-nowrap">Status</TableHead>
                    <TableHead className="text-right whitespace-nowrap">Amount Paid</TableHead>
                    <TableHead className="text-right whitespace-nowrap">Fee</TableHead>
                    <TableHead className="whitespace-nowrap">Promo</TableHead>
                    <TableHead className="whitespace-nowrap">Method</TableHead>
                    <TableHead className="whitespace-nowrap">Product ID</TableHead>
                    <TableHead className="whitespace-nowrap">Created</TableHead>
                    <TableHead className="whitespace-nowrap">Paid</TableHead>
                    <TableHead className="whitespace-nowrap">Payment ID</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((row, idx) => {
                    const amount = usd(row.amount_cents);
                    const refunded = row.refunded_amount_cents && row.refunded_amount_cents > 0
                      ? usd(row.refunded_amount_cents)
                      : null;
                    return (
                      <TableRow key={row.payment_id ?? `row-${idx}`}>
                        <TableCell className="max-w-[220px]"><PayerCell row={row} /></TableCell>
                        <TableCell><TierBadge tier={row.tier} /></TableCell>
                        <TableCell><BillingReasonBadge reason={row.billing_reason} /></TableCell>
                        <TableCell><StatusBadge row={row} /></TableCell>
                        <TableCell className="text-right tabular-nums whitespace-nowrap">
                          {amount === null ? (
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <span className="text-xs text-amber-700 underline decoration-dotted cursor-help">
                                    unavailable
                                  </span>
                                </TooltipTrigger>
                                <TooltipContent side="top" className="max-w-xs">
                                  Whop did not return a settlement amount for this payment. Not shown
                                  as $0 or list price, because either would misstate what was charged.
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          ) : (
                            <>
                              <div className="font-medium">{amount}</div>
                              {refunded && (
                                <div className="text-[11px] font-medium text-red-600">
                                  −{refunded} refunded
                                </div>
                              )}
                            </>
                          )}
                        </TableCell>
                        <TableCell className="text-right tabular-nums whitespace-nowrap text-muted-foreground text-sm">
                          {row.fee_cents !== null ? usd(row.fee_cents) : '—'}
                        </TableCell>
                        <TableCell>
                          {row.promo_code ? (
                            <Badge className="bg-indigo-100 text-indigo-800 border border-indigo-200 text-[10px] px-1.5 py-0">
                              {row.promo_code}
                            </Badge>
                          ) : (
                            <span className="text-xs text-muted-foreground">—</span>
                          )}
                        </TableCell>
                        <TableCell><MethodCell row={row} /></TableCell>
                        <TableCell className="text-xs text-muted-foreground font-mono whitespace-nowrap">
                          {row.product_id ?? '—'}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                          {row.created_at ? shortDate(row.created_at) : '—'}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                          {row.paid_at ? shortDate(row.paid_at) : '—'}
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground font-mono">
                          {row.payment_id ?? '—'}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>

            <DataPagination
              currentPage={page}
              totalPages={totalPages}
              pageSize={pageSize}
              totalItems={totalItems}
              onPageChange={setPage}
              onPageSizeChange={(s) => { setPageSize(s); setPage(1); }}
              pageSizeOptions={PAGE_SIZE_OPTIONS}
            />
          </>
        )}
      </div>
    </div>
  );
}
